import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { FileText, Download, Plus, Trash2, Receipt, Edit2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const emptyForm = {
  user_id: '',
  month: new Date().toISOString().slice(0, 7),
  basic_salary: '',
  allowances: [],
  overtime: '',
  bonus: '',
  other_earnings: [],
  epf_employee: '',
  epf_employer: '',
  socso_employee: '',
  socso_employer: '',
  eis_employee: '',
  eis_employer: '',
  pcb: '',
  other_deductions: [],
  remarks: '',
};

const AdminPayslipsSection = () => {
  const [payslips, setPayslips] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [ps, us] = await Promise.all([
        axios.get(`${API}/api/payslips`, { withCredentials: true }),
        axios.get(`${API}/api/users`, { withCredentials: true }),
      ]);
      setPayslips(ps.data);
      setTeachers(us.data.filter((u) => u.role === 'teacher'));
    } catch (e) { toast.error('Failed to load payslips'); } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      ...emptyForm,
      ...p,
      allowances: p.allowances || [],
      other_earnings: p.other_earnings || [],
      other_deductions: p.other_deductions || [],
      basic_salary: p.basic_salary ?? '',
      overtime: p.overtime ?? '',
      bonus: p.bonus ?? '',
      epf_employee: p.epf_employee ?? '',
      epf_employer: p.epf_employer ?? '',
      socso_employee: p.socso_employee ?? '',
      socso_employer: p.socso_employer ?? '',
      eis_employee: p.eis_employee ?? '',
      eis_employer: p.eis_employer ?? '',
      pcb: p.pcb ?? '',
    });
    setDialogOpen(true);
  };

  const addLineItem = (field) => {
    setForm({ ...form, [field]: [...form[field], { label: '', amount: '' }] });
  };
  const updateLineItem = (field, idx, key, value) => {
    const arr = [...form[field]];
    arr[idx] = { ...arr[idx], [key]: value };
    setForm({ ...form, [field]: arr });
  };
  const removeLineItem = (field, idx) => {
    setForm({ ...form, [field]: form[field].filter((_, i) => i !== idx) });
  };

  const savePayslip = async () => {
    if (!form.user_id || !form.month || !form.basic_salary) {
      toast.error('Teacher, month and basic salary are required');
      return;
    }
    setSaving(true);
    try {
      const toNumOrNull = (v) => (v === '' || v === null || v === undefined ? null : parseFloat(v));
      const toNumOr0 = (v) => (v === '' || v === null || v === undefined ? 0 : parseFloat(v));
      const cleanLines = (arr) => (arr || []).filter((a) => a.label && a.amount !== '').map((a) => ({ label: a.label, amount: parseFloat(a.amount) }));
      const payload = {
        user_id: form.user_id,
        month: form.month,
        basic_salary: parseFloat(form.basic_salary),
        allowances: cleanLines(form.allowances),
        overtime: toNumOr0(form.overtime),
        bonus: toNumOr0(form.bonus),
        other_earnings: cleanLines(form.other_earnings),
        epf_employee: toNumOrNull(form.epf_employee),
        epf_employer: toNumOrNull(form.epf_employer),
        socso_employee: toNumOr0(form.socso_employee),
        socso_employer: toNumOr0(form.socso_employer),
        eis_employee: toNumOr0(form.eis_employee),
        eis_employer: toNumOr0(form.eis_employer),
        pcb: toNumOr0(form.pcb),
        other_deductions: cleanLines(form.other_deductions),
        remarks: form.remarks,
      };
      if (editing) {
        await axios.put(`${API}/api/payslips/${editing.id}`, payload, { withCredentials: true });
        toast.success('Payslip updated');
      } else {
        await axios.post(`${API}/api/payslips`, payload, { withCredentials: true });
        toast.success('Payslip generated');
      }
      setDialogOpen(false);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save payslip'); } finally { setSaving(false); }
  };

  const deletePayslip = async (id) => {
    if (!window.confirm('Delete this payslip?')) return;
    try {
      await axios.delete(`${API}/api/payslips/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchAll();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const downloadPdf = async (p) => {
    try {
      const res = await axios.get(`${API}/api/payslips/${p.id}/pdf`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `payslip_${p.user_name.replace(/\s+/g, '_')}_${p.month}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Failed to download PDF'); }
  };

  const money = (v) => `RM ${Number(v || 0).toFixed(2)}`;

  if (loading) {
    return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>;
  }

  return (
    <Card className="bg-white border-slate-200" data-testid="admin-payslips-section">
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <div>
          <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Payslip Generator</CardTitle>
          <CardDescription>Generate monthly payslips for teachers (EPF, SOCSO, EIS, PCB)</CardDescription>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={openCreate} data-testid="new-payslip-button">
          <Plus className="w-4 h-4 mr-2" /> New Payslip
        </Button>
      </CardHeader>
      <CardContent>
        {payslips.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Receipt className="w-10 h-10 mx-auto mb-2 text-slate-400" />
            <p>No payslips generated yet</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Teacher</TableHead>
                <TableHead>Month</TableHead>
                <TableHead className="text-right">Gross</TableHead>
                <TableHead className="text-right">Deductions</TableHead>
                <TableHead className="text-right">Net Pay</TableHead>
                <TableHead>Issued</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="payslips-table">
              {payslips.map((p, idx) => (
                <TableRow key={p.id} data-testid={`payslip-row-${idx}`}>
                  <TableCell className="font-medium">{p.user_name}</TableCell>
                  <TableCell>{p.month}</TableCell>
                  <TableCell className="text-right">{money(p.gross_pay)}</TableCell>
                  <TableCell className="text-right text-red-600">-{money(p.total_deductions)}</TableCell>
                  <TableCell className="text-right font-semibold text-blue-700">{money(p.net_pay)}</TableCell>
                  <TableCell className="text-xs text-slate-500">
                    {p.issued_by || '—'}<br />
                    {p.created_at ? new Date(p.created_at).toLocaleDateString() : ''}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="outline" onClick={() => downloadPdf(p)} data-testid={`download-payslip-${idx}`} title="Download PDF">
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`edit-payslip-${idx}`}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => deletePayslip(p.id)} data-testid={`delete-payslip-${idx}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="payslip-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>{editing ? 'Edit' : 'Generate'} Payslip</DialogTitle>
            <DialogDescription>All amounts in Malaysian Ringgit (RM). Leave EPF blank to auto-calculate from company rate.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label>Teacher</Label>
                <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })} disabled={!!editing}>
                  <SelectTrigger data-testid="payslip-teacher-select"><SelectValue placeholder="Select teacher" /></SelectTrigger>
                  <SelectContent>{teachers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Month</Label><Input type="month" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} data-testid="payslip-month-input" /></div>
              <div><Label>Basic Salary (RM)</Label><Input type="number" step="0.01" value={form.basic_salary} onChange={(e) => setForm({ ...form, basic_salary: e.target.value })} data-testid="payslip-basic-input" /></div>
            </div>

            {/* Earnings */}
            <div className="border border-slate-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-slate-900">Earnings</h4>
                <Button type="button" size="sm" variant="outline" onClick={() => addLineItem('allowances')} data-testid="add-allowance-button">
                  <Plus className="w-3 h-3 mr-1" /> Add Allowance
                </Button>
              </div>
              {form.allowances.map((a, i) => (
                <div key={i} className="grid grid-cols-[1fr_140px_40px] gap-2 mb-2">
                  <Input placeholder="Label (e.g., Housing Allowance)" value={a.label} onChange={(e) => updateLineItem('allowances', i, 'label', e.target.value)} data-testid={`allowance-label-${i}`} />
                  <Input type="number" step="0.01" placeholder="Amount" value={a.amount} onChange={(e) => updateLineItem('allowances', i, 'amount', e.target.value)} data-testid={`allowance-amount-${i}`} />
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeLineItem('allowances', i)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                <div><Label>Overtime (RM)</Label><Input type="number" step="0.01" value={form.overtime} onChange={(e) => setForm({ ...form, overtime: e.target.value })} data-testid="payslip-overtime-input" /></div>
                <div><Label>Bonus (RM)</Label><Input type="number" step="0.01" value={form.bonus} onChange={(e) => setForm({ ...form, bonus: e.target.value })} data-testid="payslip-bonus-input" /></div>
              </div>
            </div>

            {/* Deductions */}
            <div className="border border-slate-200 rounded-lg p-4">
              <h4 className="font-semibold text-slate-900 mb-3">Statutory Deductions (Employee)</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div><Label>EPF (leave blank = auto)</Label><Input type="number" step="0.01" placeholder="Auto" value={form.epf_employee} onChange={(e) => setForm({ ...form, epf_employee: e.target.value })} data-testid="payslip-epf-ee-input" /></div>
                <div><Label>SOCSO</Label><Input type="number" step="0.01" value={form.socso_employee} onChange={(e) => setForm({ ...form, socso_employee: e.target.value })} data-testid="payslip-socso-ee-input" /></div>
                <div><Label>EIS</Label><Input type="number" step="0.01" value={form.eis_employee} onChange={(e) => setForm({ ...form, eis_employee: e.target.value })} data-testid="payslip-eis-ee-input" /></div>
                <div><Label>PCB</Label><Input type="number" step="0.01" value={form.pcb} onChange={(e) => setForm({ ...form, pcb: e.target.value })} data-testid="payslip-pcb-input" /></div>
              </div>
              <div className="mt-3 flex items-center justify-between">
                <h5 className="text-sm font-medium text-slate-700">Other Deductions</h5>
                <Button type="button" size="sm" variant="outline" onClick={() => addLineItem('other_deductions')} data-testid="add-deduction-button">
                  <Plus className="w-3 h-3 mr-1" /> Add
                </Button>
              </div>
              {form.other_deductions.map((a, i) => (
                <div key={i} className="grid grid-cols-[1fr_140px_40px] gap-2 mb-2 mt-2">
                  <Input placeholder="Label" value={a.label} onChange={(e) => updateLineItem('other_deductions', i, 'label', e.target.value)} data-testid={`deduction-label-${i}`} />
                  <Input type="number" step="0.01" placeholder="Amount" value={a.amount} onChange={(e) => updateLineItem('other_deductions', i, 'amount', e.target.value)} data-testid={`deduction-amount-${i}`} />
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeLineItem('other_deductions', i)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>

            {/* Employer contributions */}
            <div className="border border-slate-200 rounded-lg p-4">
              <h4 className="font-semibold text-slate-900 mb-3">Employer Contributions</h4>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>EPF (blank = auto)</Label><Input type="number" step="0.01" placeholder="Auto" value={form.epf_employer} onChange={(e) => setForm({ ...form, epf_employer: e.target.value })} data-testid="payslip-epf-er-input" /></div>
                <div><Label>SOCSO</Label><Input type="number" step="0.01" value={form.socso_employer} onChange={(e) => setForm({ ...form, socso_employer: e.target.value })} data-testid="payslip-socso-er-input" /></div>
                <div><Label>EIS</Label><Input type="number" step="0.01" value={form.eis_employer} onChange={(e) => setForm({ ...form, eis_employer: e.target.value })} data-testid="payslip-eis-er-input" /></div>
              </div>
            </div>

            <div>
              <Label>Remarks</Label>
              <Textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} rows={2} data-testid="payslip-remarks-input" />
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={savePayslip} disabled={saving} data-testid="save-payslip-button">
                <FileText className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : editing ? 'Update Payslip' : 'Generate Payslip'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AdminPayslipsSection;
